import { useRef, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

type Direction = 'left' | 'right' | 'top' | 'bottom' | 'scale' | 'diagonal'

interface SequenceItem {
  /** CSS selector for elements to animate */
  selector: string
  /** Direction the element enters from */
  direction: Direction
  /** Delay before this animation starts (relative to trigger) */
  delay?: number
  /** Stagger time between multiple matched elements */
  stagger?: number
  /** Animation duration */
  duration?: number
  /** Movement offset in pixels */
  offset?: number
  /** Blur amount */
  blur?: number
}

interface SequenceOptions {
  /** ScrollTrigger start position */
  start?: string
}

/**
 * Choreographed section animation with multiple elements animating in sequence
 *
 * @example
 * const sectionRef = useSectionSequence([
 *   { selector: 'h2', direction: 'left', delay: 0 },
 *   { selector: '.subtitle', direction: 'right', delay: 0.15 },
 *   { selector: '.card', direction: 'bottom', stagger: 0.1, delay: 0.3 }
 * ])
 */
export function useSectionSequence<T extends HTMLElement>(sequence: SequenceItem[], options?: SequenceOptions) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top 80%',
          toggleActions: 'play none none none',
        },
      })

      sequence.forEach((item) => {
        const elements = ref.current!.querySelectorAll(item.selector)
        if (elements.length === 0) return

        const offset = item.offset ?? 60
        const duration = item.duration ?? 0.8
        const blur = item.blur ?? 4

        // Calculate starting position based on direction
        const fromProps: gsap.TweenVars = {
          opacity: 0,
          filter: `blur(${blur}px)`,
          duration,
          ease: 'power3.out',
        }

        switch (item.direction) {
          case 'left':
            fromProps.x = -offset
            break
          case 'right':
            fromProps.x = offset
            break
          case 'top':
            fromProps.y = -offset
            break
          case 'bottom':
            fromProps.y = offset
            break
          case 'scale':
            fromProps.scale = 0.85
            fromProps.filter = `blur(${blur + 2}px)`
            break
          case 'diagonal':
            fromProps.x = -offset * 0.7
            fromProps.y = -offset * 0.7
            fromProps.scale = 0.95
            break
        }

        if (item.stagger && elements.length > 1) {
          fromProps.stagger = item.stagger
        }

        // Add to timeline at the specified delay position
        timeline.from(elements, fromProps, item.delay ?? 0)
      })
    }, ref)

    return () => ctx.revert()
  }, [sequence, options?.start])

  return ref
}

/**
 * Alternating timeline reveal - odd items from left, even from right
 * Perfect for vertical timelines or alternating content sections
 */
export function useAlternatingReveal<T extends HTMLElement>(
  selector: string,
  options?: {
    offset?: number
    duration?: number
    stagger?: number
    blur?: number
    start?: string
  }
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = ref.current!.querySelectorAll(selector)
      const offset = options?.offset ?? 80

      elements.forEach((el, index) => {
        const isOdd = index % 2 === 0

        gsap.from(el, {
          x: isOdd ? -offset : offset,
          opacity: 0,
          filter: options?.blur ? `blur(${options.blur}px)` : 'blur(4px)',
          duration: options?.duration ?? 0.9,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: options?.start ?? 'top 85%',
            toggleActions: 'play none none none',
          },
        })
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, options?.offset, options?.duration, options?.stagger, options?.blur, options?.start])

  return ref
}

/**
 * Wave stagger - diagonal wave from top-left to bottom-right
 * Great for grid layouts
 */
export function useWaveReveal<T extends HTMLElement>(
  selector: string,
  options?: {
    duration?: number
    staggerAmount?: number
    blur?: number
    start?: string
  }
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = ref.current!.querySelectorAll(selector)

      gsap.from(elements, {
        y: 40,
        x: -20,
        opacity: 0,
        filter: options?.blur ? `blur(${options.blur}px)` : 'blur(4px)',
        scale: 0.95,
        duration: options?.duration ?? 0.8,
        ease: 'power3.out',
        stagger: {
          amount: options?.staggerAmount ?? 0.6,
          grid: 'auto',
          from: 'start',
        },
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top 85%',
          toggleActions: 'play none none none',
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, options?.duration, options?.staggerAmount, options?.blur, options?.start])

  return ref
}
